import { useNavigate } from 'react-router';

import { getRoutingDataUpdateStatus } from '@/api/offlineRoutingAssets';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/sonner';
import { useAssetUpdateNotice } from '@/hooks/useAssetUpdateNotice';
import {
  dismissRoutingIncompatibleVersion,
  dismissRoutingUpdateVersion,
  getDismissedRoutingIncompatibleVersion,
  getDismissedRoutingUpdateVersion,
  routingNoticeToShow,
} from '@/utils/routingUpdateNotice';

/**
 * Tells an admin when new routing data is published.
 *
 * Shares its mechanics with the offline map notice but not its content. The
 * incompatible case below deliberately offers no install action and does not
 * mention updating the app: the desktop app auto-updates, so that would be
 * advice the user cannot act on. A mismatch means something anomalous, and only
 * the developer can resolve it.
 *
 * See TCA-26081002 and TCA-26081110.
 */
export default function RoutingDataUpdateNotice() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useAssetUpdateNotice({
    user,
    fetchUpdateStatus: getRoutingDataUpdateStatus,
    decide: (updateStatus) => routingNoticeToShow({
      updateStatus,
      user,
      dismissedUpdateVersion: getDismissedRoutingUpdateVersion(window.localStorage),
      dismissedIncompatibleVersion: getDismissedRoutingIncompatibleVersion(
        window.localStorage,
      ),
      desktopApp: true,
    }),
    notify: ({ decision, updateStatus, publishedVersion, current }) => {
      if (decision === 'incompatible') {
        return toast.warning('Routing update unavailable', {
          description: updateStatus.detail,
          cancel: {
            label: 'Dismiss',
            onClick: () => current() && dismissRoutingIncompatibleVersion(
              publishedVersion,
              window.localStorage,
            ),
          },
          duration: 15000,
        });
      }

      return toast.info('Newer offline routing data is available.', {
        description: 'Open Settings → This Device to install it.',
        action: {
          label: 'Open Settings',
          onClick: () => { if (current()) navigate('/settings?section=this-device'); },
        },
        cancel: {
          label: 'Dismiss',
          onClick: () => current() && dismissRoutingUpdateVersion(
            publishedVersion,
            window.localStorage,
          ),
        },
        duration: 15000,
      });
    },
    deps: [navigate],
  });

  return null;
}
